import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/common';
import { UploadButton } from '@/components/common';
import { Label, LABEL_TYPE } from '@/components/generic';
import { AssetScope } from '@/enums/AssetEnums';
import { useFileUpload } from '@/hooks/useFileUpload';
import { AssetDetails } from '@/pages/Event/Asset/AssetDetails';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { Asset, FilePickerType, ThemeProp } from '@/types';

type UploadContentSectionProps = {
  eventId: string;
  organizationId?: number;
  sectionTitle: string;
  sectionText: string;
  icon: JSX.Element;
  uploadButtonText: string;
  assetTag: string;
  pickerTypes: FilePickerType[];
  numberOfAssetSlots?: number;
  assets: Asset[];
  selectedAsset: Asset | null;
  setShouldRefetch: Dispatch<SetStateAction<boolean>>;
  setSelectedAsset: Dispatch<SetStateAction<Asset | null>>;
  setIsDeleteOpen: Dispatch<SetStateAction<boolean>>;
};

export const UploadContentSection = ({
  eventId,
  organizationId,
  sectionTitle,
  sectionText,
  icon,
  uploadButtonText,
  assetTag,
  pickerTypes,
  numberOfAssetSlots,
  assets,
  selectedAsset,
  setShouldRefetch,
  setSelectedAsset,
  setIsDeleteOpen
}: UploadContentSectionProps) => {
  const [t] = useTranslation('common');
  const [showAsset, setShowAsset] = useState<boolean>(false);

  const maxAssets = numberOfAssetSlots ? numberOfAssetSlots - assets.length : undefined;
  const isUploadDisabled = numberOfAssetSlots ? assets.length >= numberOfAssetSlots : false;

  const { uploadFiles, isUploading } = useFileUpload({
    eventId,
    organizationId,
    isPublic: false,
    assetScope: AssetScope.PRIVATE,
    pickerTypes,
    assetTag,
    maxFiles: maxAssets,
    onRefetchNeeded: () => {
      setShouldRefetch(true);
    }
  });

  const closeAssetModal = () => {
    setShowAsset(false);
  };

  return (
    <>
      <div className='mb-4 flex flex-row items-center border-b border-secondary-blue-gray pb-4'>
        <Label
          type={LABEL_TYPE.H3_BOLD}
          text={t(sectionTitle)}
          className='font-sans text-primary-blue'
        />

        {numberOfAssetSlots !== undefined && (
          <div className='z-10 inline-flex w-full flex-col items-end gap-2.5 px-2'>
            <div className='font-sans text-xl font-semibold text-primary-blue'>
              {`${assets.length}/${numberOfAssetSlots}`}
            </div>
          </div>
        )}
      </div>

      <Label
        type={LABEL_TYPE.PARAGRAPH2}
        text={t(sectionText, { x: numberOfAssetSlots })}
        className='pb-0 font-sans text-accent-dark-gray'
      />

      <div className='relative mt-4 flex min-h-[140px] w-full items-center justify-center rounded-[10px] border border-secondary-pale-blue-gray'>
        {assets.length === 0 ? (
          <div className='flex flex-col items-center justify-center md:min-w-[960px]'>
            {icon}

            <Label
              type={LABEL_TYPE.PARAGRAPH2}
              text={t(uploadButtonText)}
              className='pt-[7px] font-sans !text-xs font-medium text-secondary-gray'
            />
          </div>
        ) : (
          <>
            <Modal
              modalClasses='max-h-[75%] max-w-[600px] min-w-[300px] lg:min-w-[600px]'
              height='h-full'
              isOpen={showAsset}
              label=''
              onClose={closeAssetModal}>
              {showAsset && <AssetDetails asset={selectedAsset} />}
            </Modal>

            <div className='grid size-full grid-cols-2 justify-between gap-2.5 p-4 md:grid-cols-4 xl:grid-cols-5'>
              {assets.map((asset) => (
                <AssetItem
                  key={asset.id}
                  asset={asset}
                  setIsDeleteOpen={setIsDeleteOpen}
                  setShowAsset={setShowAsset}
                  setSelectedAsset={
                    setSelectedAsset as Dispatch<SetStateAction<Asset | ThemeProp | null>>
                  }
                  selectedVideoUrl=''
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className='mt-4 flex md:justify-end'>
        <UploadButton
          multiple
          icon={null}
          text={uploadButtonText}
          pickerTypes={pickerTypes}
          showMaxFileSize={false}
          isUploading={isUploading}
          disabled={isUploadDisabled || isUploading}
          onFilesSelected={uploadFiles}
          className='justify-end !rounded-[10px]'
          labelClassName='!text-sm !font-semibold'
        />
      </div>
    </>
  );
};
